import React from "react";
import { AlertTriangle, CheckCircle2, ShieldCheck } from "lucide-react";
import { timeAgo } from "./ReplicaBanner";

export const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
export const num = (n) => (n == null ? "—" : Number(n).toLocaleString());
const tot = (obj, k) => Object.values(obj || {}).reduce((a, m) => a + (m?.[k] || 0), 0);

export function Stat({ label, value, sub, testId, tone = "" }) {
    return (
        <div className="rounded border border-white/10 bg-[#050505] px-3 py-2 min-w-0" data-testid={testId}>
            <div className="label-xs">{label}</div>
            <div className={`font-display text-xl mt-1 ${tone}`}>{value}</div>
            {sub && <div className="text-[11px] text-white/40 font-mono truncate">{sub}</div>}
        </div>
    );
}

/** Last twice-daily reconciliation (06:00 + 18:00 UTC by default) and when the next one runs. */
export function ReconcileSummary({ status }) {
    const rec = status.last_reconcile;
    const ok = rec && rec.in_sync && !rec.error;
    const tone = !rec ? "border-white/10 text-white/60" : ok ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-100" : "border-amber-400/40 bg-amber-400/10 text-amber-100";
    return (
        <div className={`rounded border px-3 py-2 text-xs ${tone}`} data-testid="replica-reconcile">
            <div className="flex flex-wrap items-center gap-2">
                {!rec ? <ShieldCheck className="w-4 h-4" /> : ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                <span className="font-medium" data-testid="replica-reconcile-state">{!rec ? "Not reconciled yet" : ok ? "In sync with the hub" : rec.partial ? "Reconciliation partly failed" : "Reconciliation failed"}</span>
                {rec && <span className="text-white/50">checked {timeAgo(rec.at)} · {num(rec.checked)} keys · {num(rec.drift)} drift found{rec.drift ? " and repaired" : ""}</span>}
                <span className="ml-auto text-white/50" data-testid="replica-next-reconcile">next {fmt(status.next_reconcile_at)} ({(status.reconcile_hours_utc || []).map((h) => `${String(h).padStart(2, "0")}:00`).join(" + ")} UTC)</span>
            </div>
            {rec?.error && <div className="mt-1 break-all text-amber-200" data-testid="replica-reconcile-error">{rec.error}</div>}
            {rec?.retry && (
                <div className="mt-1 text-sky-200" data-testid="replica-reconcile-retry">
                    Trying again automatically at {fmt(rec.retry.at)} (follow-up {rec.retry.attempt} of {rec.retry.max})
                </div>
            )}
            {rec?.collections && rec.drift > 0 && (
                <div className="mt-1 text-white/50 font-mono" data-testid="replica-reconcile-repairs">
                    fetched {num(tot(rec.collections, "missing") + tot(rec.collections, "stale"))} · removed {num(tot(rec.collections, "extra_hub_origin"))} · pushed {num(tot(rec.collections, "extra_local"))}
                </div>
            )}
        </div>
    );
}

export function CollectionsTable({ status }) {
    return (
        <div className="min-w-0 overflow-x-auto">
            <div className="label-xs mb-2">Collections · local count · pull @ / push @</div>
            <table className="w-full text-xs" data-testid="replica-collections">
                <tbody>
                    {(status.collections || []).map((c) => (
                        <tr key={c.coll} className="border-t border-white/5">
                            <td className="py-1 text-white/70">{c.label}</td>
                            <td className="py-1 text-right font-mono text-white/80">{num(status.counts?.[c.coll])}</td>
                            <td className="py-1 text-right font-mono text-white/40 whitespace-nowrap">@{num(c.cursor)} / {num(c.push_cursor)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export function RecentRuns({ runs }) {
    return (
        <div className="min-w-0">
            <div className="label-xs mb-2">Recent runs</div>
            <ul className="space-y-1 text-xs" data-testid="replica-runs">
                {(runs || []).slice(0, 6).map((r) => (
                    <li key={r.run_id} className="flex flex-wrap items-center gap-2 border-t border-white/5 py-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${r.status === "done" ? "bg-emerald-400" : r.status === "failed" ? "bg-rose-400" : "bg-sky-400"}`} />
                        <span className="text-white/70">{fmt(r.started_at)}</span>
                        <span className="text-white/40">{r.trigger}{r.reconcile ? " · reconcile" : ""}</span>
                        <span className="ml-auto font-mono text-white/60 break-all">
                            {r.status === "failed" ? r.error : `↓${num(tot(r.merged, "added") + tot(r.merged, "updated"))} ↑${num(tot(r.pushed, "sent"))}`}
                        </span>
                    </li>
                ))}
                {!runs?.length && <li className="text-white/40">No runs yet.</li>}
            </ul>
        </div>
    );
}
